import React, { useState, useMemo } from 'react';
import { Search, User, Building2, Phone, ArrowUpDown } from 'lucide-react';
import { Input } from '../ui/input';
import RadixButton from '../ui/RadixButton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table"

/**
 * ClientsTab component with Individual and Corporate sub-tabs
 * Rendered in tabular form
 * @param {Object} props - Component props
 * @param {Object} props.clientInfo - Client info with individual and corporate arrays
 * @returns {JSX.Element} ClientsTab component
 */
const ClientsTab = ({ clientInfo }) => {
  const [activeSubTab, setActiveSubTab] = useState('individual');
  const [searchTerm, setSearchTerm] = useState('');
  const [sorting, setSorting] = useState({ column: null, direction: 'asc' });

  const { individual = [], corporate = [] } = clientInfo || {};

  // Filter clients based on search term (name, email, phone, clientId)
  const filteredIndividual = useMemo(() => {
    if (!searchTerm.trim()) return individual;
    const term = searchTerm.toLowerCase();
    return individual.filter(
      (client) =>
        (client.id && client.id.toString().includes(term)) ||
        (client.name && client.name.toLowerCase().includes(term)) ||
        (client.email && client.email.toLowerCase().includes(term)) ||
        (client.phone && client.phone.includes(term))
    );
  }, [individual, searchTerm]);

  const filteredCorporate = useMemo(() => {
    if (!searchTerm.trim()) return corporate;
    const term = searchTerm.toLowerCase();
    return corporate.filter(
      (client) =>
        (client.id && client.id.toString().includes(term)) ||
        (client.name && client.name.toLowerCase().includes(term)) ||
        (client.email && client.email.toLowerCase().includes(term)) ||
        (client.phone && client.phone.includes(term)) ||
        (client.companyName && client.companyName.toLowerCase().includes(term))
    );
  }, [corporate, searchTerm]);

  // Sort function
  const sortData = (data, column, direction) => {
    if (!column) return data;
    
    return [...data].sort((a, b) => {
      let aVal = a[column];
      let bVal = b[column];
      
      if (column === 'id') {
        aVal = parseInt(aVal) || 0;
        bVal = parseInt(bVal) || 0;
      } else {
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
      }
      
      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleSort = (column) => {
    setSorting(prev => ({
      column,
      direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const SortHeader = ({ column, children }) => (
    <RadixButton
      variant="ghost"
      onClick={() => handleSort(column)}
      className="h-8 px-2 lg:px-3"
    >
      {children}
      <ArrowUpDown className="ml-2 h-4 w-4" />
    </RadixButton>
  );

  // Get current data based on active tab
  const currentData = activeSubTab === 'individual' ? filteredIndividual : filteredCorporate;
  const sortedData = sortData(currentData, sorting.column, sorting.direction);

  // Phone formatter
  const formatPhone = (phone, countryCode) => {
    if (!phone) return '-';
    return countryCode ? `+${countryCode}${phone}` : phone;
  };

  return (
    <div className="space-y-4">
      {/* Search Bar */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          type="text"
          placeholder="Search by ID, name, email, or phone..."
          className="pl-9"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Sub-tabs */}
      <div className="border-b border-gray-200">
        <div className="flex gap-4">
          <button
            onClick={() => setActiveSubTab('individual')}
            className={`flex items-center gap-2 py-3 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeSubTab === 'individual'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <User className="w-4 h-4" />
            Individual
            <span className="ml-1 px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
              {filteredIndividual.length}
            </span>
          </button>
          <button
            onClick={() => setActiveSubTab('corporate')}
            className={`flex items-center gap-2 py-3 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeSubTab === 'corporate'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Building2 className="w-4 h-4" />
            Corporate
            <span className="ml-1 px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
              {filteredCorporate.length}
            </span>
          </button>
        </div>
      </div>

      {/* Table Content */}
      <div className="border rounded-lg overflow-hidden">
        {sortedData.length === 0 ? (
          <div className="text-center py-12 bg-gray-50">
            {activeSubTab === 'individual' ? (
              <User className="w-12 h-12 mx-auto text-gray-400 mb-3" />
            ) : (
              <Building2 className="w-12 h-12 mx-auto text-gray-400 mb-3" />
            )}
            <p className="text-gray-600">
              {searchTerm
                ? 'No clients match your search'
                : `No ${activeSubTab} clients found`}
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="h-10 px-3 py-2">
                  <SortHeader column="id">Client ID</SortHeader>
                </TableHead>
                <TableHead className="h-10 px-3 py-2">
                  <SortHeader column="name">Name</SortHeader>
                </TableHead>
                {activeSubTab === 'corporate' && (
                  <TableHead className="h-10 px-3 py-2">
                    <SortHeader column="companyName">Company</SortHeader>
                  </TableHead>
                )}
                <TableHead className="h-10 px-3 py-2">Email</TableHead>
                <TableHead className="h-10 px-3 py-2">Phone</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedData.map((client) => (
                <TableRow key={client.id} className="h-12 hover:bg-gray-50">
                  <TableCell className="px-3 py-2 font-medium">
                    {client.id}
                  </TableCell>
                  <TableCell className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                        {activeSubTab === 'individual' ? (
                          <User className="w-4 h-4 text-indigo-600" />
                        ) : (
                          <Building2 className="w-4 h-4 text-purple-600" />
                        )}
                      </div>
                      <span className="font-medium">{client.name || 'N/A'}</span>
                    </div>
                  </TableCell>
                  {activeSubTab === 'corporate' && (
                    <TableCell className="px-3 py-2">
                      <span className="px-2 py-1 bg-purple-50 text-purple-700 text-xs font-medium rounded-full">
                        {client.companyName || '-'}
                      </span>
                    </TableCell>
                  )}
                  <TableCell className="px-3 py-2">
                    {client.email ? (
                      <span className="text-blue-600 text-sm">{client.email}</span>
                    ) : (
                      <span className="text-gray-400 text-sm">-</span>
                    )}
                  </TableCell>
                  <TableCell className="px-3 py-2">
                    <div className="flex items-center gap-1 text-sm">
                      {client.phone ? (
                        <>
                          <Phone className="w-3 h-3 text-gray-400" />
                          {formatPhone(client.phone, client.countryCode)}
                        </>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
};

export default ClientsTab;
